import type { MessageKey } from "@colorglo/i18n";
import type { KpiValue, NetworkFilter } from "../api/network.ts";
import type { Translate } from "./locale.ts";
import { formatMoney, Temporal, today } from "./time.ts";

/**
 * Turning the API's KPI values into strings a person reads.
 *
 * Kept out of the components because the same value is rendered in four places — the KPI
 * tile, the league table, the sparkline tooltip and the trend chart's axis — and four
 * formatters is four chances for one of them to round differently and make the page look
 * like it disagrees with itself.
 */

/** The ranges the period picker offers, in days. */
export const RANGE_DAYS = [7, 30, 90, 365] as const;
export type RangeDays = (typeof RANGE_DAYS)[number];

export const RANGE_LABELS: Record<RangeDays, MessageKey> = {
  7: "network.range.7",
  30: "network.range.30",
  90: "network.range.90",
  365: "network.range.365",
};

/**
 * A range ending today, in the reader's own zone.
 *
 * HQ sits in Stockholm and the network spans one hour today; the rollup is bucketed by each
 * franchise's *local* date, so the browser's date is the right end for a filter and the
 * server does the per-location arithmetic.
 */
export function rangeEndingToday(days: RangeDays, timezone: string): { from: string; to: string } {
  const end = today(timezone);
  return { from: end.subtract({ days: days - 1 }).toString(), to: end.toString() };
}

/** The metric a KPI tile is about, as a message key. */
export function metricLabel(metric: string): MessageKey {
  return `network.metric.${metric}` as MessageKey;
}

/**
 * Minutes, at the scale the metric actually lands on.
 *
 * Lead time across a network is tens of thousands of minutes — "44 025 min" is a number
 * nobody can read. First-response time is usually a few hours. One formatter, three scales,
 * chosen by magnitude rather than by which metric it is.
 */
export function formatMinutes(minutes: number, t: Translate, locale: string): string {
  const number = (value: number, decimals: number) =>
    new Intl.NumberFormat(locale, {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    }).format(value);

  if (minutes < 90) return t("network.unit.minutes", { count: number(minutes, 0) });
  if (minutes < 60 * 48) return t("network.unit.hours", { count: number(minutes / 60, 1) });
  return t("network.unit.days", { count: number(minutes / 1440, 1) });
}

export function formatPercent(value: number, t: Translate, locale: string): string {
  return t("network.unit.percent", {
    value: new Intl.NumberFormat(locale, {
      minimumFractionDigits: 1,
      maximumFractionDigits: 1,
    }).format(value),
  });
}

export function formatCount(value: number, locale: string): string {
  return new Intl.NumberFormat(locale).format(Math.round(value));
}

/**
 * One KPI as a string.
 *
 * Money is the interesting case: it is an array, one entry per currency, and the entries are
 * joined with a separator rather than summed. Two amounts side by side is the whole point —
 * "1 240 000 kr · 180 000 kr" is an honest answer and "1 420 000 kr" is not (CLAUDE.md).
 */
export function formatKpi(kpi: KpiValue, t: Translate, locale: string): string {
  if (!kpi.applicable) return t("network.metric.not_applicable");

  if (kpi.unit === "money") {
    const money = kpi.money ?? [];
    if (money.length === 0) return "—";
    return money
      .map((entry) => formatMoney(entry.amount_minor, entry.currency, locale))
      .join(" · ");
  }

  if (kpi.value === null) return "—";
  if (kpi.unit === "percent") return formatPercent(kpi.value, t, locale);
  if (kpi.unit === "minutes") return formatMinutes(kpi.value, t, locale);
  return formatCount(kpi.value, locale);
}

/** A scalar for a chart axis or a sparkline. Money collapses to the first currency's amount. */
export function scalarOf(
  value: number | null,
  money: readonly { amount_minor: number }[] | null,
): number | null {
  if (value !== null) return value;
  const first = money?.[0];
  return first === undefined ? null : first.amount_minor / 100;
}

export type DeltaTone = "better" | "worse" | "flat" | "none";

export interface Delta {
  readonly tone: DeltaTone;
  readonly label: string;
}

/**
 * A period-over-period change, phrased as better or worse rather than up or down.
 *
 * The sign is not the story: a no-show rate falling five points is *good*, and an arrow
 * pointing down next to a red number says the opposite. `direction` comes from the server's
 * metric definition, so the reading is the same in the KPI tile and the league table.
 */
export function describeDelta(
  percent: number | null,
  direction: "up_is_good" | "down_is_good",
  t: Translate,
  locale: string,
): Delta {
  if (percent === null) return { tone: "none", label: t("network.delta.none") };

  // Under a tenth of a per cent is noise, and rendering it as "+0,0 %" invites someone to
  // read a rounding artefact as a trend.
  if (Math.abs(percent) < 0.1) return { tone: "flat", label: t("network.delta.flat") };

  const improved = direction === "up_is_good" ? percent > 0 : percent < 0;
  const magnitude = new Intl.NumberFormat(locale, {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
    signDisplay: "always",
  }).format(percent);

  return {
    tone: improved ? "better" : "worse",
    label: t(improved ? "network.delta.better" : "network.delta.worse", {
      value: `${magnitude} %`,
    }),
  };
}

/** Minutes to hours, for the overview's "3 av 8 h bokat idag". */
export function hoursOf(minutes: number, locale: string): string {
  return new Intl.NumberFormat(locale, { maximumFractionDigits: 1 }).format(minutes / 60);
}

/**
 * How loaded a location is today, 0–1, or null when it is closed.
 *
 * Null and zero are different answers and the map draws them differently: a workshop that is
 * shut for a public holiday is not a workshop with nothing booked.
 */
export function loadOf(location: {
  readonly booked_minutes: number;
  readonly capacity_minutes: number;
}): number | null {
  if (location.capacity_minutes === 0) return null;
  return Math.min(location.booked_minutes / location.capacity_minutes, 1);
}

/** `2026-03-01` → `1 mars`, for a chart axis that has to fit forty of them. */
export function shortDate(iso: string, locale: string): string {
  return new Intl.DateTimeFormat(locale, { day: "numeric", month: "short" }).format(
    new Date(`${iso}T00:00:00Z`),
  );
}

/** An instant from the API, as a readable local timestamp. */
export function formatTimestamp(iso: string, locale: string, timezone: string): string {
  return new Intl.DateTimeFormat(locale, {
    dateStyle: "short",
    timeStyle: "short",
    timeZone: timezone,
  }).format(new Date(Temporal.Instant.from(iso).epochMilliseconds));
}

/** Is anything narrowing this filter? Drives the "showing everything" copy. */
export function isUnfiltered(filter: NetworkFilter): boolean {
  return filter.country === null && filter.franchiseIds.length === 0 && filter.serviceId === null;
}
